<template>
  <v-dialog :model-value="show" persistent max-width="760">
    <v-card color="surface" rounded="lg" class="pa-6">
      <div class="text-center mb-6">
        <h2 class="text-h5 font-weight-bold mb-2">Welcome to MissionSquad</h2>
        <p class="text-body-2 text-medium-emphasis mb-0">
          How would you like to get started? You can switch anytime later.
        </p>
      </div>

      <v-row dense>
        <v-col cols="12" sm="6">
          <v-card
            color="background"
            variant="elevated"
            elevation="4"
            height="100%"
            class="pa-5 d-flex flex-column ga-3 mode-card"
            @click="choose('simple')"
          >
            <v-avatar color="primary" variant="tonal" rounded="lg" size="46">
              <v-icon icon="mdi-rocket-launch-outline" />
            </v-avatar>
            <span class="text-subtitle-1 font-weight-bold">I’m just getting started</span>
            <span class="text-body-2 text-medium-emphasis flex-grow-1">
              A simpler workspace — chat, automations, and the essentials. No workflow graphs or config to learn.
            </span>
            <span class="text-caption text-disabled text-uppercase">Non-technical</span>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6">
          <v-card
            color="background"
            variant="elevated"
            elevation="4"
            height="100%"
            class="pa-5 d-flex flex-column ga-3 mode-card"
            @click="choose('advanced')"
          >
            <v-avatar color="primary" variant="tonal" rounded="lg" size="46">
              <v-icon icon="mdi-tune-variant" />
            </v-avatar>
            <span class="text-subtitle-1 font-weight-bold">I’m a technical user</span>
            <span class="text-body-2 text-medium-emphasis flex-grow-1">
              Full access — workflows, factories, schedules, embeddings and every advanced control.
            </span>
            <span class="text-caption text-disabled text-uppercase">Technical</span>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import type { UserMode } from '@/router/dashboardTabs'

@Options({
  props: {
    // Set by DashboardView once the initial data fetch has resolved. Gating on it stops the dialog
    // flashing on a cold cache before provider state is confirmed.
    ready: { type: Boolean, default: false }
  },
  computed: {
    ...mapGetters(['authenticated', 'awaitingModeChoice'])
  }
})
export default class UserModeDialog extends Vue {
  ready!: boolean
  authenticated!: boolean | null
  awaitingModeChoice!: boolean

  // Prompt only mid-onboarding: a brand-new account (DashboardView latched modeChoicePending when it
  // loaded with no mode and no providers) whose first provider is now connected. Legacy users never
  // set the flag, so they never see this — an unset mode behaves as advanced everywhere.
  get show(): boolean {
    return this.authenticated === true && this.ready && this.awaitingModeChoice
  }

  choose(mode: UserMode) {
    this.$store.dispatch('setUserMode', mode)
    // Resume onboarding: the provider-setup flow closed the config chat to yield to this choice —
    // reopen it so the configuration assistant picks up where it left off.
    this.$store.dispatch('toggleConfigChat', true)
  }
}
</script>

<style scoped lang="scss">
.mode-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  cursor: pointer;
  transition: border-color 0.16s, transform 0.16s;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
    transform: translateY(-2px);
  }
}
</style>
